import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../../api/api";
import styles from "./Painel.module.scss";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Field,
  Input,
  Textarea,
  Btn,
  AlertModal,
  ConfirmModal,
} from "../../components/Modal/Modal";
import mStyles from "../../components/Modal/Modal.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCESS_URL = "https://cliente.fluxoeasy.com.br";
const TRIAL_DAYS = 14;

const STATUS_FILTER_OPTIONS = [
  { value: "todos", label: "Todos os status" },
  { value: "ativo", label: "Ativo" },
  { value: "inativo", label: "Inativo" },
];

const VENCIMENTO_FILTER_OPTIONS = [
  { value: "todos", label: "Todos os vencimentos" },
  { value: "vencido", label: "Vencido" },
  { value: "a-vencer", label: "A vencer" },
];

// ─── Mensagens pré-prontas ────────────────────────────────────────────────────

const buildMensagens = (cliente) => ({
  boasVindas: {
    label: "👋 Boas-vindas",
    cor: "btnSilt",
    texto: (login = "", senha = "") =>
`🎉 Parabéns por adquirir o *FluxoEasy*! Seja muito bem-vindo(a)!

Agora você conta com um sistema completo para facilitar a gestão do seu negócio e ganhar mais organização no dia a dia. 🚀

✅ Orçamentos rápidos e profissionais
✅ Controle de estoque
✅ Painel de vendas
✅ Painel de aluguel
✅ Agendamentos
✅ Calendário integrado
✅ Registro de vendas
✅ Múltiplos usuários

Tudo pensado para trazer mais praticidade, controle e crescimento para sua empresa.

🔐 *Seus dados de acesso:*
🌐 Link: ${ACCESS_URL}
👤 Login: ${login}
🔑 Senha: ${senha}

📲 Em caso de dúvidas ou suporte, estamos à disposição!
Obrigado por confiar no *FluxoEasy*! 💙`,
  },

  vencimentoProximo: {
    label: "⏰ Venc. próximo",
    cor: "btnRenovar",
    texto: () =>
`Olá, *${cliente?.nome_proprietario || cliente?.nome}*! 👋

Passando para avisar que o vencimento da sua assinatura do *FluxoEasy* está se aproximando.

📅 Vencimento: *${cliente?.data_vencimento ? new Date(cliente.data_vencimento).toLocaleDateString("pt-BR") : "—"}*

Para garantir a continuidade do sistema sem interrupções, entre em contato conosco para renovar. 😊

📲 Estamos à disposição para ajudar!
*FluxoEasy* 💙`,
  },

  pesquisaSatisfacao: {
    label: "⭐ Satisfação",
    cor: "btnCopia",
    texto: () =>
`Olá, *${cliente?.nome_proprietario || cliente?.nome}*! 😊

Gostaríamos de saber: *o que você está achando do FluxoEasy?*

Sua opinião é muito importante para continuarmos melhorando o sistema e entregando cada vez mais valor para o seu negócio. 🚀

👇 Responda com uma nota de 1 a 5:
⭐ 1 - Muito ruim
⭐⭐ 2 - Ruim
⭐⭐⭐ 3 - Regular
⭐⭐⭐⭐ 4 - Bom
⭐⭐⭐⭐⭐ 5 - Excelente

Pode nos contar também o que podemos melhorar — adoramos feedback! 💬

Obrigado por confiar no *FluxoEasy*! 💙`,
  },

  cobranca: {
    label: "💰 Cobrança",
    cor: "btnDesativar",
    texto: () =>
`Olá, *${cliente?.nome_proprietario || cliente?.nome}*! 👋

Identificamos que a sua assinatura do *FluxoEasy* está em aberto.

📅 Vencimento: *${cliente?.data_vencimento ? new Date(cliente.data_vencimento).toLocaleDateString("pt-BR") : "—"}*

Para evitar a suspensão do acesso ao sistema, pedimos que regularize o pagamento o quanto antes.

Em caso de dúvidas ou para negociar, entre em contato conosco! 😊

*FluxoEasy* 💙`,
  },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString("pt-BR") : "-";

// Gera senha legível no celular: palavra-número-palavra (ex: "ceu-47-rio")
// Sem letras ambíguas (0/O, 1/l/I) e sem caracteres especiais difíceis de digitar
const generatePassword = () => {
  const palavras = [
    "mar","sol","rio","ceu","lua","paz","bem","boa","voz","luz",
    "flor","mel","asa","voo","dom","fé","rei","lei","cor","tom",
    "raiz","obra","puro","neve","azul","bom","prata","ouro","fino","novo",
  ];
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const num = String(Math.floor(Math.random() * 90) + 10); // 10–99
  return `${pick(palavras)}-${num}-${pick(palavras)}`;
};

// Gera login único: clienteId + nome → "42.carlos.mendes"
// Prefixo com o ID garante que dois clientes com mesmo nome de proprietário
// nunca colidam no banco (login é global, não por cliente).
const generateLogin = (nome = '', clienteId = '') => {
  const slug = nome
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zs]/g, '')
    .trim()
    .replace(/s+/g, '.')
    .slice(0, 20);
  return clienteId ? `${clienteId}.${slug}` : slug;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const addMonths = (date, months) => {
  const d = new Date(date ?? new Date());
  d.setMonth(d.getMonth() + months);
  return d;
};

const getDaysUntilVencimento = (dataVencimento) => {
  if (!dataVencimento) return null;
  return (new Date(dataVencimento) - new Date()) / 86_400_000;
};

const getVencimentoStatus = (dataVencimento) => {
  const diff = getDaysUntilVencimento(dataVencimento);
  if (diff === null) return "";
  if (diff < 0) return "vencido";
  if (diff <= 5) return "perto-vencer";
  return "ok";
};

const getSituacaoLabel = (dataVencimento) => {
  if (!dataVencimento) return { texto: "-", classe: "" };
  return new Date(dataVencimento) < new Date()
    ? { texto: "Vencido", classe: styles.tagVencido }
    : { texto: "A vencer", classe: styles.tagAVencer };
};

// Retorna alertas de vencimento para clientes ativos que vencem em 1 ou 3 dias
const getVencimentoAlertas = (clientes) => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return clientes
    .filter((c) => c.ativo && c.data_vencimento)
    .map((c) => {
      const venc = new Date(c.data_vencimento);
      venc.setHours(0, 0, 0, 0);
      const diff = Math.round((venc - hoje) / 86_400_000);
      if (diff === 1) return { cliente: c, diff, tipo: "vespera" };
      if (diff === 3) return { cliente: c, diff, tipo: "tres-dias" };
      return null;
    })
    .filter(Boolean);
};

// ─── Banner de alertas de vencimento ─────────────────────────────────────────

const VencimentoAlertas = ({ clientes, onRenovar }) => {
  const alertas = useMemo(() => getVencimentoAlertas(clientes), [clientes]);
  if (alertas.length === 0) return null;

  return (
    <div className={styles.alertasBanner}>
      {alertas.map(({ cliente, diff, tipo }) => (
        <div
          key={cliente.id}
          className={`${styles.alertaItem} ${styles[`alerta--${tipo}`]}`}
        >
          <span className={styles.alertaIcon}>
            {tipo === "vespera" ? "🚨" : "⚠️"}
          </span>
          <span className={styles.alertaTexto}>
            {tipo === "vespera" ? (
              <>
                <strong>Véspera de vencimento!</strong>{" "}
                <span>
                  {cliente.nome_proprietario
                    ? `${cliente.nome_proprietario} — `
                    : ""}
                  {cliente.nome} vence <strong>amanhã</strong>
                </span>
              </>
            ) : (
              <>
                <strong>Vence em 3 dias:</strong>{" "}
                <span>
                  {cliente.nome_proprietario
                    ? `${cliente.nome_proprietario} — `
                    : ""}
                  {cliente.nome} vence em{" "}
                  <strong>{formatDate(cliente.data_vencimento)}</strong>
                </span>
              </>
            )}
          </span>
          <button
            type="button"
            className={styles.alertaBtnRenovar}
            onClick={() => onRenovar(cliente)}
          >
            Renovar agora
          </button>
        </div>
      ))}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const FilterBar = ({
  filtro,
  setFiltro,
  filtroStatus,
  setFiltroStatus,
  filtroVencimento,
  setFiltroVencimento,
  onCriar,
}) => (
  <div
    className={styles.filterBar}
    role="search"
    aria-label="Filtros de clientes"
  >
    <input
      type="search"
      className={styles.searchInput}
      placeholder="Buscar cliente..."
      value={filtro}
      onChange={(e) => setFiltro(e.target.value)}
      aria-label="Buscar cliente por nome"
    />
    <select
      className={styles.select}
      value={filtroStatus}
      onChange={(e) => setFiltroStatus(e.target.value)}
      aria-label="Filtrar por status"
    >
      {STATUS_FILTER_OPTIONS.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
    <select
      className={styles.select}
      value={filtroVencimento}
      onChange={(e) => setFiltroVencimento(e.target.value)}
      aria-label="Filtrar por vencimento"
    >
      {VENCIMENTO_FILTER_OPTIONS.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
    <button type="button" className={styles.btnPrimary} onClick={onCriar}>
      <PlusIcon /> Criar Cliente
    </button>
  </div>
);

const StatsBar = ({ total, ativos, vencidos }) => (
  <div
    className={styles.statsBar}
    role="status"
    aria-label="Resumo de clientes"
  >
    <StatCard label="Total" value={total} variant="neutral" />
    <StatCard label="Ativos" value={ativos} variant="success" />
    <StatCard label="Vencidos" value={vencidos} variant="danger" />
  </div>
);

const StatCard = ({ label, value, variant }) => (
  <div className={`${styles.statCard} ${styles[`statCard--${variant}`]}`}>
    <span className={styles.statValue}>{value}</span>
    <span className={styles.statLabel}>{label}</span>
  </div>
);

const ClienteRow = ({
  cliente,
  onEditar,
  onAtivarDesativar,
  onRenovar,
  onMensagem,
  onCriarSilt,
  onCriarAdmin,
  onDadosInternos,
}) => {
  const situacao = getSituacaoLabel(cliente.data_vencimento);
  const vencStatus = getVencimentoStatus(cliente.data_vencimento);
  const msgs = buildMensagens(cliente);

  return (
    <tr className={styles.row}>
      <td className={styles.tdNome}>
        <span className={styles.nomeText}>{cliente.nome}</span>
        <span className={styles.cnpjText}>{cliente.cnpj_cpf}</span>
        {cliente.tem_usuario_online && (
          <span
            className={styles.badgeOnline}
            title="Algum usuário está online agora"
          >
            🟢 Online
          </span>
        )}
      </td>
      <td>
        <span
          className={cliente.ativo ? styles.badgeAtivo : styles.badgeInativo}
        >
          {cliente.ativo ? "Ativo" : "Inativo"}
        </span>
      </td>
      <td>
        <span className={`${styles.tag} ${situacao.classe}`}>
          {situacao.texto}
        </span>
      </td>
      <td className={`${styles.tdVenc} ${styles[vencStatus]}`}>
        {formatDate(cliente.data_vencimento)}
      </td>
      <td>{cliente.nome_proprietario || "—"}</td>
      <td>{cliente.contato_cliente || "—"}</td>
      <td className={styles.tdAcoes}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnEditar}`}
          onClick={() => onEditar(cliente)}
        >
          Editar
        </button>
        <button
          type="button"
          className={`${styles.btn} ${cliente.ativo ? styles.btnDesativar : styles.btnAtivar}`}
          onClick={() => onAtivarDesativar(cliente)}
        >
          {cliente.ativo ? "Desativar" : "Ativar"}
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnRenovar}`}
          onClick={() => onRenovar(cliente)}
        >
          Renovar
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSilt}`}
          onClick={() => onCriarSilt(cliente)}
        >
          SILT
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnAdmin}`}
          onClick={() => onCriarAdmin(cliente)}
        >
          Admin
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnAdmin}`}
          onClick={() => onDadosInternos(cliente)}
        >
          🔒 Interno
        </button>

        <span className={styles.btnDivider} />

        {Object.entries(msgs).map(([key, msg]) => (
          <button
            key={key}
            type="button"
            className={`${styles.btn} ${styles[msg.cor]}`}
            onClick={() => onMensagem(cliente, key)}
            aria-label={`${msg.label} para ${cliente.nome}`}
          >
            {msg.label}
          </button>
        ))}
      </td>
    </tr>
  );
};

const EmptyState = ({ filtro }) => (
  <tr>
    <td colSpan={10} className={styles.emptyState}>
      {filtro
        ? `Nenhum cliente encontrado para "${filtro}"`
        : "Nenhum cliente cadastrado."}
    </td>
  </tr>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// ─── Modal: Criar Cliente (simplificado) ──────────────────────────────────────
// Campos: nome da empresa, nome do proprietário, contato interno.
// CNPJ, e-mail, endereço etc. ficam a cargo do próprio cliente na outra página.

const ClienteFormModal = ({ open, onClose, onSave, title, defaults = {} }) => {
  const isEdit = !!defaults.nome;

  const [form, setForm] = useState({
    nome: "",
    nome_proprietario: "",
    contato_cliente: "",
    // campos extras só aparecem no modo edição (já existem no banco)
    email: "",
    cnpj_cpf: "",
    telefone: "",
    endereco: "",
  });

  useEffect(() => {
    if (open)
      setForm({
        nome: defaults.nome ?? "",
        nome_proprietario: defaults.nome_proprietario ?? "",
        contato_cliente: defaults.contato_cliente ?? "",
        email: defaults.email ?? "",
        cnpj_cpf: defaults.cnpj_cpf ?? "",
        telefone: defaults.telefone ?? "",
        endereco: defaults.endereco ?? "",
      });
  }, [open]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const handleSave = () => {
    if (!form.nome.trim()) return;
    onSave(form);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="md">
      <ModalHeader
        title={title}
        subtitle={
          isEdit
            ? `Editando dados de ${defaults.nome}`
            : "Preencha os dados essenciais do novo cliente"
        }
        icon="🏢"
        onClose={onClose}
      />
      <ModalBody>
        {/* Campos essenciais — sempre visíveis */}
        <Field label="Nome da empresa *">
          <Input
            value={form.nome}
            onChange={set("nome")}
            placeholder="Razão social ou nome fantasia"
          />
        </Field>
        <Field label="Nome do proprietário">
          <Input
            value={form.nome_proprietario}
            onChange={set("nome_proprietario")}
            placeholder="Ex: Carlos Mendes"
          />
        </Field>
        <Field label="Contato interno (WhatsApp)">
          <Input
            value={form.contato_cliente}
            onChange={set("contato_cliente")}
            placeholder="(21) 99999-9999"
          />
        </Field>

        {/* Campos extras — só no modo edição, pois o cliente preenche pelo próprio painel */}
        {isEdit && (
          <>
            <div
              style={{
                margin: "16px 0 8px",
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "#9CA3AF",
              }}
            >
              Dados adicionais (gerenciados pelo cliente)
            </div>
            <Field label="Email">
              <Input
                value={form.email}
                onChange={set("email")}
                placeholder="email@empresa.com"
                type="email"
              />
            </Field>
            <Field label="CNPJ / CPF">
              <Input
                value={form.cnpj_cpf}
                onChange={set("cnpj_cpf")}
                placeholder="00.000.000/0001-00"
              />
            </Field>
            <Field label="Telefone">
              <Input
                value={form.telefone}
                onChange={set("telefone")}
                placeholder="(21) 99999-9999"
              />
            </Field>
            <Field label="Endereço">
              <Input
                value={form.endereco}
                onChange={set("endereco")}
                placeholder="Rua, número, cidade"
              />
            </Field>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>
          Cancelar
        </Btn>
        <Btn variant="primary" onClick={handleSave}>
          {isEdit ? "Salvar alterações" : "Criar cliente"}
        </Btn>
      </ModalFooter>
    </Modal>
  );
};

// ─── Modal: Dados Internos ────────────────────────────────────────────────────

const DadosInternosModal = ({ open, onClose, onSave, cliente }) => {
  const [form, setForm] = useState({
    nome_proprietario: "",
    contato_cliente: "",
  });

  useEffect(() => {
    if (open && cliente)
      setForm({
        nome_proprietario: cliente.nome_proprietario ?? "",
        contato_cliente: cliente.contato_cliente ?? "",
      });
  }, [open, cliente]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const handleSave = () => {
    onSave({
      nome_proprietario: form.nome_proprietario.trim() || null,
      contato_cliente: form.contato_cliente.trim() || null,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <ModalHeader
        title="Dados internos"
        subtitle={cliente?.nome}
        icon="🔒"
        onClose={onClose}
      />
      <ModalBody>
        <div className={mStyles.infoBadge}>
          🔒 Visível apenas para você (superadmin) — o cliente não tem acesso a
          estas informações
        </div>
        <Field label="Nome do proprietário">
          <Input
            value={form.nome_proprietario}
            onChange={set("nome_proprietario")}
            placeholder="Ex: Carlos Mendes"
          />
        </Field>
        <Field label="Contato do cliente">
          <Input
            value={form.contato_cliente}
            onChange={set("contato_cliente")}
            placeholder="Ex: (21) 99999-0000"
          />
        </Field>
      </ModalBody>
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>
          Cancelar
        </Btn>
        <Btn variant="primary" onClick={handleSave}>
          Salvar dados internos
        </Btn>
      </ModalFooter>
    </Modal>
  );
};

// ─── Modal: Renovar ───────────────────────────────────────────────────────────

const RenovarModal = ({ open, onClose, onSave, cliente }) => {
  const [meses, setMeses] = useState(1);
  return (
    <Modal open={open} onClose={onClose} size="sm">
      <ModalHeader
        title="Renovar assinatura"
        subtitle={cliente?.nome}
        icon="🔄"
        onClose={onClose}
      />
      <ModalBody>
        <p
          style={{
            fontSize: "0.875rem",
            color: "#6B7280",
            marginBottom: "16px",
            marginTop: 0,
          }}
        >
          Selecione o período de renovação:
        </p>
        <div className={mStyles.renewalOptions}>
          {[
            { v: 1, label: "1 mês", desc: "Mensal" },
            { v: 12, label: "12 meses", desc: "Anual" },
          ].map(({ v, label, desc }) => (
            <button
              key={v}
              type="button"
              className={`${mStyles.renewalOption} ${meses === v ? mStyles["renewalOption--active"] : ""}`}
              onClick={() => setMeses(v)}
            >
              <strong>{label}</strong>
              <span>{desc}</span>
            </button>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Btn variant="secondary" onClick={onClose}>
          Cancelar
        </Btn>
        <Btn
          variant="warning"
          onClick={() => {
            onSave(meses);
            onClose();
          }}
        >
          Renovar por {meses === 1 ? "1 mês" : "12 meses"}
        </Btn>
      </ModalFooter>
    </Modal>
  );
};

// ─── Modal: Mensagem ──────────────────────────────────────────────────────────

const MensagemModal = ({ open, onClose, title, mensagem, cliente }) => {
  const [copied, setCopied] = useState(false);
  const [text, setText] = useState("");

  useEffect(() => {
    if (open) {
      setText(mensagem);
      setCopied(false);
    }
  }, [open, mensagem]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Usa contato_cliente como prioridade, depois telefone do cadastro
  const tel = cliente?.contato_cliente || cliente?.telefone;
  const whatsappLink = tel
    ? `https://wa.me/55${tel.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`
    : null;
  const emailLink = cliente?.email
    ? `mailto:${cliente.email}?subject=FluxoEasy&body=${encodeURIComponent(text)}`
    : null;

  return (
    <Modal open={open} onClose={onClose} size="md">
      <ModalHeader title={title} icon="💬" onClose={onClose} />
      <ModalBody>
        <div className={mStyles.messageActions}>
          {whatsappLink && (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className={mStyles.whatsappBtn}
            >
              📱 Abrir no WhatsApp
            </a>
          )}
          {emailLink && (
            <a href={emailLink} className={mStyles.emailBtn}>
              ✉️ E-mail
            </a>
          )}
        </div>
        <Field label="Mensagem — edite se necessário">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ minHeight: "200px" }}
          />
        </Field>
      </ModalBody>
      <ModalFooter>
        {copied && (
          <span className={mStyles.copyFeedback}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Copiado!
          </span>
        )}
        <Btn variant="secondary" onClick={onClose}>
          Fechar
        </Btn>
        <Btn variant="primary" onClick={handleCopy}>
          Copiar mensagem
        </Btn>
      </ModalFooter>
    </Modal>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const PainelPage = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroVencimento, setFiltroVencimento] = useState("todos");

  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [mensagem, setMensagem] = useState(null);

  const [clienteModal, setClienteModal] = useState({
    open: false,
    defaults: {},
    title: "",
  });
  const [dadosModal, setDadosModal] = useState({ open: false, cliente: null });
  const [renovarModal, setRenovarModal] = useState({
    open: false,
    cliente: null,
  });
  const [adminCreated, setAdminCreated] = useState(null);
  const [clienteEditando, setClienteEditando] = useState(null);

  const showAlert = (type, title, text) => setAlert({ type, title, text });
  const showConfirm = (opts) => setConfirm(opts);

  const fetchClientes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/clientes/all", {
        headers: getAuthHeader(),
      });
      setClientes(data);
    } catch (err) {
      console.error("fetchClientes:", err);
      showAlert("error", "Erro", "Não foi possível carregar os clientes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const clientesFiltrados = useMemo(() => {
    return clientes.filter((c) => {
      const nomeOK = c.nome.toLowerCase().includes(filtro.toLowerCase());
      const statusOK =
        filtroStatus === "todos"
          ? true
          : filtroStatus === "ativo"
            ? c.ativo
            : !c.ativo;
      const venc = c.data_vencimento ? new Date(c.data_vencimento) : null;
      const hoje = new Date();
      const vencimentoOK =
        filtroVencimento === "todos"
          ? true
          : filtroVencimento === "vencido"
            ? venc && venc < hoje
            : venc && venc >= hoje;
      return nomeOK && statusOK && vencimentoOK;
    });
  }, [clientes, filtro, filtroStatus, filtroVencimento]);

  const stats = useMemo(
    () => ({
      total: clientes.length,
      ativos: clientes.filter((c) => c.ativo).length,
      vencidos: clientes.filter(
        (c) => getVencimentoStatus(c.data_vencimento) === "vencido",
      ).length,
    }),
    [clientes],
  );

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleCriarCliente = useCallback(() => {
    setClienteModal({ open: true, defaults: {}, title: "Criar Cliente" });
  }, []);

  const handleCriarClienteSave = useCallback(
    async (formValues) => {
      try {
        // Envia apenas os campos que o backend createCliente espera
        const payload = {
          nome: formValues.nome,
          nome_proprietario: formValues.nome_proprietario || null,
          contato_cliente: formValues.contato_cliente || null,
        };
        const { data: novoCliente } = await api.post("/clientes", payload, {
          headers: getAuthHeader(),
        });
        showAlert(
          "success",
          "Cliente criado!",
          `${formValues.nome} foi criado com ${TRIAL_DAYS} dias de teste.`,
        );
        setAdminCreated({ open: true, novoCliente, formValues });
        fetchClientes();
      } catch {
        showAlert("error", "Erro", "Não foi possível criar o cliente.");
      }
    },
    [fetchClientes],
  );

  const handleConfirmCriarAdmin = useCallback(async () => {
    if (!adminCreated) return;
    const { novoCliente, formValues } = adminCreated;
    const login = generateLogin(formValues.nome_proprietario || formValues.nome, novoCliente.id);
    const senha = generatePassword();

    try {
      await api.post(
        "/usuarios/register",
        {
          nome: formValues.nome_proprietario || formValues.nome,
          login,
          senha,
          cargo: "Admin",
          nivel_acesso: "admin",
          cliente_id: novoCliente.id,
        },
        { headers: getAuthHeader() },
      );

      // Monta mensagem de boas-vindas já pronta para WhatsApp
      const msgs = buildMensagens({ ...novoCliente, ...formValues });
      setMensagem({
        title: "👋 Boas-vindas",
        texto: msgs.boasVindas.texto(login, senha),
        cliente: {
          ...novoCliente,
          contato_cliente:
            formValues.contato_cliente || novoCliente.contato_cliente,
          telefone: formValues.telefone,
        },
      });
    } catch {
      showAlert("error", "Erro", "Não foi possível criar o usuário admin.");
    }
    setAdminCreated(null);
  }, [adminCreated]);

  const openEditar = useCallback((cliente) => {
    setClienteEditando(cliente);
    setClienteModal({ open: true, defaults: cliente, title: "Editar Cliente" });
  }, []);

  const handleEditarSave = useCallback(
    async (formValues, clienteAtual) => {
      try {
        await api.put(
          `/clientes/${clienteAtual.id}`,
          { ...formValues, ativo: clienteAtual.ativo },
          { headers: getAuthHeader() },
        );
        fetchClientes();
        showAlert("success", "Atualizado!", "Dados do cliente salvos.");
      } catch {
        showAlert("error", "Erro", "Não foi possível atualizar o cliente.");
      }
    },
    [fetchClientes],
  );

  const handleAtivarDesativar = useCallback(
    (cliente) => {
      showConfirm({
        type: "warning",
        title: `${cliente.ativo ? "Desativar" : "Ativar"} cliente?`,
        text: `Confirma ${cliente.ativo ? "desativar" : "ativar"} "${cliente.nome}"?`,
        confirmText: "Confirmar",
        onConfirm: async () => {
          try {
            await api.put(
              `/clientes/${cliente.id}`,
              { ...cliente, ativo: !cliente.ativo },
              { headers: getAuthHeader() },
            );
            fetchClientes();
          } catch {
            showAlert("error", "Erro", "Não foi possível atualizar o status.");
          }
        },
      });
    },
    [fetchClientes],
  );

  const handleRenovar = useCallback((cliente) => {
    setRenovarModal({ open: true, cliente });
  }, []);

  const handleRenovarSave = useCallback(
    async (meses, cliente) => {
      const novaData = addMonths(cliente.data_vencimento, meses);
      try {
        await api.put(
          `/clientes/${cliente.id}`,
          { ...cliente, data_vencimento: novaData, ativo: true },
          { headers: getAuthHeader() },
        );
        fetchClientes();
        showAlert(
          "success",
          "Renovado!",
          `Cliente renovado por ${meses === 1 ? "1 mês" : "12 meses"}.`,
        );
      } catch {
        showAlert("error", "Erro", "Não foi possível renovar o cliente.");
      }
    },
    [fetchClientes],
  );

  const handleMensagem = useCallback(async (cliente, tipoMensagem) => {
    const msgs = buildMensagens(cliente);
    const msg = msgs[tipoMensagem];

    if (tipoMensagem === "boasVindas") {
      try {
        const { data: usuario } = await api.get(
          `/usuarios/admin/${cliente.id}`,
          {
            headers: getAuthHeader(),
            validateStatus: (s) => s < 500,
          },
        );
        setMensagem({
          title: msg.label,
          texto: msg.texto(usuario?.login ?? "—", usuario?.senha ?? "—"),
          cliente,
        });
      } catch {
        setMensagem({ title: msg.label, texto: msg.texto("—", "—"), cliente });
      }
      return;
    }

    setMensagem({ title: msg.label, texto: msg.texto(), cliente });
  }, []);

  const handleCriarSilt = useCallback(async (cliente) => {
    const login = `silt.${generateLogin(cliente.nome_proprietario || cliente.nome, cliente.id)}`;
    const senha = generatePassword();
    try {
      await api.post(
        "/usuarios/register",
        {
          nome: `SILT ${cliente.nome}`,
          login,
          senha,
          cargo: "Silt",
          nivel_acesso: "admin",
          cliente_id: cliente.id,
        },
        { headers: getAuthHeader() },
      );
      // Texto limpo para WhatsApp
      setMensagem({
        title: "Usuário SILT criado!",
        texto: `*Acesso SILT — ${cliente.nome}*\n\n🌐 Link: ${ACCESS_URL}\n👤 Login: ${login}\n🔑 Senha: ${senha}`,
        cliente,
      });
    } catch {
      showAlert("error", "Erro", "Não foi possível criar o usuário SILT.");
    }
  }, []);

  const handleCriarAdmin = useCallback(async (cliente) => {
    const login = generateLogin(cliente.nome_proprietario || cliente.nome, cliente.id);
    const senha = generatePassword();
    try {
      await api.post(
        "/usuarios/register",
        {
          nome: cliente.nome,
          login,
          senha,
          cargo: "Administrador",
          nivel_acesso: "admin",
          cliente_id: cliente.id,
        },
        { headers: getAuthHeader() },
      );
      // Texto limpo para WhatsApp
      setMensagem({
        title: "Admin gerado com sucesso",
        texto: `*Acesso Admin — ${cliente.nome}*\n\n🌐 Link: ${ACCESS_URL}\n👤 Login: ${login}\n🔑 Senha: ${senha}`,
        cliente,
      });
    } catch {
      showAlert("error", "Erro", "Erro ao gerar admin.");
    }
  }, []);

  const handleDadosInternos = useCallback((cliente) => {
    setDadosModal({ open: true, cliente });
  }, []);

  const handleDadosInternosSave = useCallback(
    async (value, cliente) => {
      try {
        await api.put(`/clientes/${cliente.id}/dados-internos`, value, {
          headers: getAuthHeader(),
        });
        fetchClientes();
        showAlert("success", "Salvo!", "Dados internos atualizados.");
      } catch {
        showAlert(
          "error",
          "Erro",
          "Não foi possível salvar os dados internos.",
        );
      }
    },
    [fetchClientes],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <main className={styles.page} aria-label="Gerenciamento de clientes">
      <AlertModal
        open={!!alert}
        onClose={() => setAlert(null)}
        type={alert?.type}
        title={alert?.title}
        text={alert?.text}
      />
      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm ?? (() => {})}
        type={confirm?.type ?? "warning"}
        title={confirm?.title}
        text={confirm?.text}
        confirmText={confirm?.confirmText}
      />
      <MensagemModal
        open={!!mensagem}
        onClose={() => setMensagem(null)}
        title={mensagem?.title}
        mensagem={mensagem?.texto ?? ""}
        cliente={mensagem?.cliente}
      />
      <ClienteFormModal
        open={clienteModal.open}
        onClose={() => setClienteModal((p) => ({ ...p, open: false }))}
        onSave={(formValues) => {
          if (clienteModal.title.includes("Criar"))
            handleCriarClienteSave(formValues);
          else handleEditarSave(formValues, clienteEditando);
        }}
        title={clienteModal.title}
        defaults={clienteModal.defaults}
      />
      <ConfirmModal
        open={!!adminCreated?.open}
        onClose={() => setAdminCreated(null)}
        onConfirm={handleConfirmCriarAdmin}
        type="question"
        title="Criar usuário admin?"
        text="Deseja criar um usuário administrador para este cliente agora?"
        confirmText="Criar admin"
      />
      <DadosInternosModal
        open={dadosModal.open}
        onClose={() => setDadosModal({ open: false, cliente: null })}
        onSave={(value) => handleDadosInternosSave(value, dadosModal.cliente)}
        cliente={dadosModal.cliente}
      />
      <RenovarModal
        open={renovarModal.open}
        onClose={() => setRenovarModal({ open: false, cliente: null })}
        onSave={(meses) => handleRenovarSave(meses, renovarModal.cliente)}
        cliente={renovarModal.cliente}
      />

      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Clientes</h1>
          <p className={styles.pageSubtitle}>
            Gerencie todos os clientes do FluxoEasy
          </p>
        </div>
      </div>

      {/* ── Alertas de vencimento ── */}
      <VencimentoAlertas clientes={clientes} onRenovar={handleRenovar} />

      <StatsBar {...stats} />

      <div className={styles.tableCard}>
        <FilterBar
          filtro={filtro}
          setFiltro={setFiltro}
          filtroStatus={filtroStatus}
          setFiltroStatus={setFiltroStatus}
          filtroVencimento={filtroVencimento}
          setFiltroVencimento={setFiltroVencimento}
          onCriar={handleCriarCliente}
        />
        <div className={styles.tableWrapper}>
          <table className={styles.table} aria-label="Lista de clientes">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Status</th>
                <th>Situação</th>
                <th>Vencimento</th>
                <th>Proprietário</th>
                <th>Contato interno</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className={styles.emptyState}>
                    <span className={styles.loadingDots}>Carregando</span>
                  </td>
                </tr>
              ) : clientesFiltrados.length === 0 ? (
                <EmptyState filtro={filtro} />
              ) : (
                clientesFiltrados.map((c) => (
                  <ClienteRow
                    key={c.id}
                    cliente={c}
                    onEditar={openEditar}
                    onAtivarDesativar={handleAtivarDesativar}
                    onRenovar={handleRenovar}
                    onMensagem={handleMensagem}
                    onCriarSilt={handleCriarSilt}
                    onCriarAdmin={handleCriarAdmin}
                    onDadosInternos={handleDadosInternos}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.tableFooter} aria-live="polite">
          Exibindo <strong>{clientesFiltrados.length}</strong> de{" "}
          <strong>{clientes.length}</strong> clientes
        </div>
      </div>
    </main>
  );
};

export default PainelPage;